import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  boardDashboardSchema,
  dashboardTileChartTypePatchSchema,
  dashboardTileCreateBodySchema,
  dashboardTileFacetPatchSchema,
  dashboardTileSchema,
  type ApiError,
  type BoardDashboard,
  type DashboardAggregation,
  type DashboardBucket,
  type DashboardChartType,
  type DashboardFacet,
  type DashboardTile,
  type DashboardTileCreateBody,
} from '@molt/shared';
import { del, emptyResponseSchema, get, patch, post } from './api';
import {
  EMPTY_BOARD_FILTER,
  boardFilterCacheKey,
  boardFilterSearchParams,
  type BoardFilterSelection,
} from './board-filter';
import type { LocaleFormattingTranslate } from './locale-formatting';
import { queryKeys } from './query-keys';

export function boardDashboardQueryString(
  filter: BoardFilterSelection = EMPTY_BOARD_FILTER,
): string {
  return boardFilterSearchParams(filter).toString();
}

export function getBoardDashboard(
  boardId: string,
  filter: BoardFilterSelection = EMPTY_BOARD_FILTER,
): Promise<BoardDashboard> {
  const query = boardDashboardQueryString(filter);
  const suffix = query === '' ? '' : `?${query}`;
  return get(
    `/boards/${encodeURIComponent(boardId)}/dashboard${suffix}`,
    boardDashboardSchema,
  );
}

export function createDashboardTile(
  boardId: string,
  body: DashboardTileCreateBody,
): Promise<DashboardTile> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/dashboard/tiles`,
    dashboardTileSchema,
    dashboardTileCreateBodySchema.parse(body),
  );
}

export function updateDashboardTileFacet(
  tileId: string,
  facet: DashboardFacet,
): Promise<DashboardTile> {
  return patch(
    `/dashboard/tiles/${encodeURIComponent(tileId)}/facet`,
    dashboardTileSchema,
    dashboardTileFacetPatchSchema.parse({ facet }),
  );
}

export function updateDashboardTileChartType(
  tileId: string,
  chartType: DashboardChartType,
): Promise<DashboardTile> {
  return patch(
    `/dashboard/tiles/${encodeURIComponent(tileId)}/chart-type`,
    dashboardTileSchema,
    dashboardTileChartTypePatchSchema.parse({ chartType }),
  );
}

export function deleteDashboardTile(tileId: string): Promise<null> {
  return del(`/dashboard/tiles/${encodeURIComponent(tileId)}`, emptyResponseSchema);
}

export function boardDashboardQueryOptions(
  boardId: string,
  filter: BoardFilterSelection = EMPTY_BOARD_FILTER,
): UseQueryOptions<BoardDashboard, ApiError> {
  return {
    queryKey: queryKeys.boards.dashboard(boardId, boardFilterCacheKey(filter)),
    queryFn: () => getBoardDashboard(boardId, filter),
  };
}

export function useCreateDashboardTile(
  boardId: string,
): UseMutationResult<DashboardTile, Error, DashboardTileCreateBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => createDashboardTile(boardId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.dashboard(boardId),
      });
    },
  });
}

export function useUpdateDashboardTileFacet(
  boardId: string,
): UseMutationResult<
  DashboardTile,
  Error,
  { readonly tileId: string; readonly facet: DashboardFacet }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tileId, facet }) => updateDashboardTileFacet(tileId, facet),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.dashboard(boardId),
      });
    },
  });
}

export function useUpdateDashboardTileChartType(
  boardId: string,
): UseMutationResult<
  DashboardTile,
  Error,
  { readonly tileId: string; readonly chartType: DashboardChartType }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tileId, chartType }) =>
      updateDashboardTileChartType(tileId, chartType),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.dashboard(boardId),
      });
    },
  });
}

export function useDeleteDashboardTile(
  boardId: string,
): UseMutationResult<null, Error, { readonly tileId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tileId }) => deleteDashboardTile(tileId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.dashboard(boardId),
      });
    },
  });
}

/**
 * FE-80.1 — the two wire→label tables, routed.
 *
 * ⚠ `t` is the REQUIRED first parameter, with no default, and every key is
 * spelled inline at its own literal `t()` call rather than reached through the
 * record's own values. A key handed to `t` through a variable renders
 * correctly and type-checks, and is invisible to the source anchor the routing
 * pin counts and orphan-checks with.
 *
 * `Record<DashboardFacet, string>` is kept so a new member of the wire union
 * fails `tsc` here rather than rendering a blank label.
 */
export function dashboardFacetLabel(
  t: LocaleFormattingTranslate,
  facet: DashboardFacet,
): string {
  const labels: Record<DashboardFacet, string> = {
    list: t('boardDashboard.facet.list'),
    member: t('boardDashboard.facet.member'),
    label: t('boardDashboard.facet.label'),
    due_date: t('boardDashboard.facet.dueDate'),
  };
  return labels[facet];
}

export function dashboardChartTypeLabel(
  t: LocaleFormattingTranslate,
  chartType: DashboardChartType,
): string {
  return chartType === 'bar'
    ? t('boardDashboard.chartType.bar')
    : t('boardDashboard.chartType.pie');
}

export function dashboardAggregationForTile(
  dashboard: BoardDashboard,
  tile: DashboardTile,
): DashboardAggregation | null {
  return (
    dashboard.aggregations.find((aggregation) => aggregation.facet === tile.facet) ?? null
  );
}

export function dashboardBucketPattern(bucket: DashboardBucket): string | undefined {
  if (!('label' in bucket)) return undefined;
  const colour = bucket.label.colour;
  if (colour === 'colourless') return undefined;
  return colour.replace(/_(subtle|bold)$/, '');
}

export function dashboardBucketSwatchKey(bucket: DashboardBucket): string | undefined {
  if ('label' in bucket) return bucket.label.colour;
  if ('due' in bucket) return bucket.due;
  if ('member' in bucket) return bucket.member?.initialsColour ?? 'unassigned';
  if ('list' in bucket) return bucket.list.id;
  return undefined;
}

export function dashboardBucketPercent(
  bucket: Pick<DashboardBucket, 'count'>,
  buckets: readonly Pick<DashboardBucket, 'count'>[],
): number {
  const max = Math.max(...buckets.map((item) => item.count), 0);
  if (max <= 0) return 0;
  return Math.round((bucket.count / max) * 100);
}
